// Port of src/app/components/nav-bar/home/upload-responses/upload-responses.component.{ts,html,scss}
// (mypart-upload-responses). [@pixelateInOutDelay] → CSS enter class from
// react/src/animations/pixelateInOutDelay.ts. The WritableSignal input became a
// plain value prop; the sendToMainEvent output became onSendToMain.

import { useEffect, useRef, useState } from 'react';
import { PIXELATE_IN_OUT_DELAY_ENTER_CLASS } from '../../animations/pixelateInOutDelay';
import './UploadResponses.scss';

export interface UploadResponsesProps {
  fileUploadResponse: { key: string; value: string }[] | undefined;
  onSendToMain: (value: string | undefined) => void;
}

// Seconds the "Recommended" prompt waits before it auto-runs. Longer than the
// original 10s so users can actually read all three suggestions before the top
// one fires (QA July 2026 feature request).
const COUNTDOWN_START = 20;

export default function UploadResponses({ fileUploadResponse, onSendToMain }: UploadResponsesProps) {
  const [countdown, setCountdown] = useState(COUNTDOWN_START);
  // True once the countdown has been cancelled (by a page click) — the badge
  // then drops the "starting in Ns" text and reads just "Recommended".
  const [countdownStopped, setCountdownStopped] = useState(false);
  const countdownIntervalRef = useRef<ReturnType<typeof setInterval> | undefined>(undefined);
  // Latest values for the interval callback (mirrors Angular instance fields).
  const fileUploadResponseRef = useRef(fileUploadResponse);
  fileUploadResponseRef.current = fileUploadResponse;
  const onSendToMainRef = useRef(onSendToMain);
  onSendToMainRef.current = onSendToMain;

  const clearCountdown = () => {
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = undefined;
    }
  };

  // ngOnInit → startCountdown; ngOnDestroy → clearCountdown.
  useEffect(() => {
    setCountdown(COUNTDOWN_START);
    countdownIntervalRef.current = setInterval(() => {
      setCountdown(v => {
        const next = v - 1;
        if (next <= 0) {
          clearCountdown();
          const first = fileUploadResponseRef.current?.[0];
          if (first) {
            onSendToMainRef.current(first.value);
          }
        }
        return next;
      });
    }, 1000);
    return clearCountdown;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cancel the auto-run as soon as the user clicks ANYWHERE on the page — they
  // want to read/choose for themselves (QA July 2026 feature request). This only
  // stops the timer; it does not select any prompt. Bubble clicks select via
  // their own onClick (selectOption) and also land here, which is harmless.
  useEffect(() => {
    const cancelOnPageClick = () => {
      clearCountdown();
      setCountdownStopped(true);
    };
    document.addEventListener('click', cancelOnPageClick);
    return () => document.removeEventListener('click', cancelOnPageClick);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectOption = (value: string | undefined) => {
    clearCountdown();
    onSendToMain(value);
  };

  const renderBubbles = (extraBubbleClass: string) => (
    <>
      {fileUploadResponse?.map((prompt, index) => {
        const first = index === 0;
        return (
          <div key={prompt.key}
               onClick={() => selectOption(prompt.value)}
               className={`bubble${extraBubbleClass}${first ? ' recommended' : ''}`}>
            {first && (
              <span className="recommended-badge">
                {countdownStopped ? 'Recommended' : `Recommended - starting in ${countdown}s`}
              </span>
            )}
            <p title={prompt.key}>{prompt.value}</p>
          </div>
        );
      })}
    </>
  );

  return (
    <div className="upload-responses">
      <section className="big-screen">
        <section className={`bottom-boxes bottom-boxes-bubble ${PIXELATE_IN_OUT_DELAY_ENTER_CLASS}`}>
          {renderBubbles('')}
        </section>
      </section>

      <section className="small-screen">
        <section className={`small-bubbles ${PIXELATE_IN_OUT_DELAY_ENTER_CLASS}`}>
          {renderBubbles(' p-4')}
        </section>
      </section>
    </div>
  );
}
